import { $, html } from 'rune-ts';
import { Input_Base } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Base.ts';
import { InputEditor } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/type.ts';
import klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input.module.scss';
import { font } from '@services/MarppleShop/shared/typography';
import { ValidateField } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { classes } from '@services/MarppleShop/shared/util';
import { StudioCalendar } from '@services/MarppleShop/marppleShopStudioApp/components/cells/StudioCalendar/StudioCalendar.ts';
import format from 'date-fns/format/index.js';
import { DeleteDateEvent } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/events.ts';

interface InputDateData extends InputEditor {
  value: string | null;
  placeholder?: string;
  max_date?: Date;
  min_date?: Date;
}

export class Input_Date extends Input_Base<InputDateData> {
  private show_calendar = false;
  private studioCalendar = new StudioCalendar({
    type: 'single',
    enable_time: true,
    max_date: this.data.max_date,
    min_date: this.data.min_date,
  });

  private calendarStates = [
    {
      valid: () => this.show_calendar,
      run: (element: Element) => {
        element.classList.add(klass.show);
      },
    },
    {
      valid: () => !this.show_calendar,
      run: (element: Element) => {
        element.classList.remove(klass.show);
      },
    },
  ];

  protected inputTemplate() {
    return html`
      <div class="${klass.date_input_container}">
        <div
          class="${classes(klass.date_input_wrap, {
            [klass.active]: '',
          })}"
        >
          <span class="${klass.date_input} ${font('14_medium')}"
            >${(this.data.value || this.data.placeholder) ?? ''}</span
          >
          ${this.makeIconHtml()}
        </div>
        <div
          class="${classes(klass.calendar_container, {
            [klass.show]: this.show_calendar,
          })}"
        >
          ${this.studioCalendar}
        </div>
      </div>
    `;
  }

  override titleRightHtml() {
    return html`<div class="${klass.delete_date} ${font('12_medium')}">지우기</div>`;
  }

  override onRender() {
    this.delegate('click', `.${klass.date_input_wrap}`, () => {
      this.show_calendar = !this.show_calendar;
      this.applyCalendarState();
    });

    this.delegate('click', `.${klass.delete_date}`, () => {
      this.show_calendar = false;
      this.applyCalendarState();
      this.dispatchEvent(DeleteDateEvent, { bubbles: true });
    });
  }

  private applyCalendarState() {
    $(this.element())
      .find(`.${klass.calendar_container}`)
      ?.chain((el) => {
        this.calendarStates.find((state) => state.valid())?.run(el);
      });
  }

  private handleClickOutside = (event: MouseEvent | TouchEvent) => {
    if (this.show_calendar && !this.element().contains(event.target as Node)) {
      this.show_calendar = false;
      this.applyCalendarState();
    }
  };

  protected override onMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('touchstart', this.handleClickOutside);
  }

  protected override onUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('touchstart', this.handleClickOutside);
  }

  private makeIconHtml() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <g clip-path="url(#clip0_16798_5221)">
          <path
            d="M17.0823 3.54154H14.6073V2.39154C14.6073 1.99154 14.2823 1.6582 13.874 1.6582C13.4656 1.6582 13.1406 1.9832 13.1406 2.39154V3.54154H6.83229V2.39154C6.83229 1.99154 6.50729 1.6582 6.09896 1.6582C5.69063 1.6582 5.36563 1.9832 5.36563 2.39154V3.54154H2.89062C2.19896 3.54154 1.64062 4.09987 1.64062 4.79154V17.1499C1.64062 17.8082 2.19896 18.3332 2.89062 18.3332H17.0573C17.749 18.3332 18.3073 17.7999 18.3073 17.1499V4.79154C18.3073 4.09987 17.749 3.54154 17.0573 3.54154H17.0823ZM16.874 8.74987H3.12396V5.41654C3.12396 5.1832 3.30729 4.99987 3.54062 4.99987H5.39062V6.14154C5.39062 6.54154 5.71562 6.87487 6.12396 6.87487C6.53229 6.87487 6.85729 6.54987 6.85729 6.14154V4.99987H13.1656V6.14154C13.1656 6.54154 13.4906 6.87487 13.899 6.87487C14.3073 6.87487 14.6323 6.54987 14.6323 6.14154V4.99987H16.4823C16.7156 4.99987 16.899 5.1832 16.899 5.41654V8.74987H16.874Z"
            fill="#A2A2A2"
          />
        </g>
        <defs>
          <clipPath id="clip0_16798_5221">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </svg>
    `;
  }

  public setDate(date?: Date | null) {
    this.data.value = date ? format(date, 'yyyy-MM-dd HH:mm') : (this.data.placeholder ?? '');
    $(this.element()).find(`.${klass.date_input}`)?.setTextContent(this.data.value);
  }

  public setMaxDate(date?: Date | null) {
    this.studioCalendar.setMaxDate(date);
  }

  public setMinDate(date?: Date | null) {
    this.studioCalendar.setMinDate(date);
  }

  public getName(): ValidateField {
    return this.data.name;
  }

  public override setError(): void {
    return void 0;
  }

  public getValue() {
    return this.data.value;
  }
}
